import { formatClock } from "./agenda-schedule";
import {
  formatMinutesLeft,
  sessionProgress,
  type RunningSession,
} from "./now-model";

/**
 * The session that is on as one large colour card: the room chip, the title,
 * a short description when the data has one, and how far through it is.
 *
 * The card is the Dusk live gradient with the strong edge layer and a glowing
 * dot, so every word on it is white. It no longer takes its room's fill: one
 * card on the page is the live one, and it looks the same whichever room it
 * is in.
 */
export function NowLiveCard({
  session,
  now,
  timeZone,
}: {
  session: RunningSession;
  now: Date;
  timeZone: string;
}) {
  const description = session.item.description?.trim();
  const { percent, minutesLeft } = sessionProgress(
    session.start,
    session.end,
    now,
  );

  return (
    <section
      aria-label="On now"
      className="flex min-w-0 flex-col gap-3 rounded-2xl border-edge border-lit-edge bg-dusk-live px-5 pb-6 pt-5 text-white shadow-lit-strong"
      data-testid="now-live-card"
    >
      <p
        className="flex max-w-full items-center gap-2 self-start rounded-full border-2 border-white/70 px-3 py-1 text-[0.8125rem] font-bold uppercase tracking-[0.08em] wrap-anywhere"
        data-testid="now-live-room"
      >
        <span
          aria-hidden
          className="h-2 w-2 shrink-0 rounded-full bg-hb-yellow shadow-lit-dot motion-safe:animate-pulse"
          data-testid="now-live-dot"
        />
        <span className="min-w-0">
          On now{session.room && ` · ${session.room}`}
        </span>
      </p>
      <h2
        className="font-display text-[2rem] font-normal leading-[1.05] wrap-anywhere sm:text-[2.75rem]"
        data-testid="now-live-title"
      >
        {session.item.title}
      </h2>
      {description && (
        <p
          className="line-clamp-3 text-[1.0625rem] leading-snug wrap-anywhere"
          data-testid="now-live-description"
        >
          {description}
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <div
          role="progressbar"
          aria-label="Session progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          className="h-2 rounded-full bg-white/25"
        >
          <div
            className="h-2 rounded-full bg-hb-yellow"
            style={{ width: `${percent}%` }}
            data-testid="now-progress-fill"
          />
        </div>
        <div className="flex justify-between gap-2 text-sm font-semibold tabular-nums">
          <span data-testid="now-live-start">
            {formatClock(session.start, timeZone)}
          </span>
          <span className="text-center" data-testid="now-live-left">
            {formatMinutesLeft(minutesLeft)}
          </span>
          <span data-testid="now-live-end">
            {formatClock(session.end, timeZone)}
          </span>
        </div>
      </div>
    </section>
  );
}
